import React, { createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useDashboardBriefing } from '../features/dashboard/hooks/useDashboardBriefing';
import type { EnrichedBriefingData } from '../features/dashboard/hooks/useDashboardBriefing';
import { useIsMobile } from '../hooks/useMediaQuery';
import { useAuth } from './AuthContext';
import type { AuthUser } from './AuthContext';

interface BriefingContextValue {
  briefingData: EnrichedBriefingData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  isClient: boolean;
}

const BriefingContext = createContext<BriefingContextValue | null>(null);

/**
 * Routes where briefing data is used — only fetch/poll on these pages.
 * Matches any path ending with /dashboard (covers /dashboard, /admin/dashboard,
 * and any future role-specific dashboard paths) plus the AI assistant routes.
 */
const BRIEFING_ROUTES = ['/ai-assistant', '/assistant', '/calendar'];
const DASHBOARD_SUFFIX = '/dashboard';

/** Client roles/character types that should never trigger briefing fetch. */
const CLIENT_CHARACTER_TYPES = ['client', 'parent_of_client', 'part_of_couple'];

function isClientRole(user: AuthUser | false | null): boolean {
  if (!user) return false;
  if (user.demo_mode) {
    return CLIENT_CHARACTER_TYPES.includes(user.character_type as string);
  }
  const roles = Array.isArray(user.roles) ? user.roles : (user.role ? [user.role] : []);
  return roles.includes('client');
}

/**
 * BriefingProvider — shared enriched briefing data for Dashboard + AI Assistant.
 *
 * Wraps `useDashboardBriefing` so both pages share a single fetch + polling cycle
 * instead of each page fetching independently. Desktop only, non-client only,
 * and only on routes that actually consume briefing data (Dashboard + AI Assistant).
 */
export function BriefingProvider({ children }: { children: React.ReactNode }) {
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const onBriefingRoute =
    pathname === DASHBOARD_SUFFIX || pathname.endsWith(DASHBOARD_SUFFIX) ||
    BRIEFING_ROUTES.some(r => pathname.startsWith(r));
  const enabled = !isMobile && onBriefingRoute && !isClientRole(user);
  const { briefingData, loading, error, refetch, isClient } = useDashboardBriefing({ enabled });

  return (
    <BriefingContext.Provider value={{ briefingData, loading, error, refetch, isClient }}>
      {children}
    </BriefingContext.Provider>
  );
}

/**
 * useBriefing — consume shared briefing data from the nearest BriefingProvider.
 * Returns a safe default when called outside the provider.
 */
export function useBriefing(): BriefingContextValue {
  const ctx = useContext(BriefingContext);
  return ctx ?? { briefingData: null, loading: false, error: null, refetch: () => {}, isClient: false };
}
